import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { hasEdgeTeamScope, SCOPE_EDGE_COMPANIES, SCOPE_EDGE_USERS } from '../config/apps'
import { StackMark } from './Lamp'
import ThemeToggle from './ThemeToggle'

/**
 * EDGE 포털(조직 관리) 셸. 여기는 특정 앱이 아니라 계정·조직을 다루는 플랫폼 층이므로
 * EdgeManager(왼쪽 레일)와 다른 골격으로 구분한다 — 테마와 무관하게 어두운 상단 바 + 가운데 정렬 본문.
 * 상단 바는 AppSelect(런처)와 공유한다 (EdgeBar).
 */
export function EdgeBar({ children, right }: { children?: React.ReactNode; right?: React.ReactNode }) {
  return (
    <header className="bg-bar text-white">
      <div className="max-w-5xl mx-auto px-6 h-14 flex items-center gap-8">
        <Link to="/apps" className="flex items-center gap-3 shrink-0">
          <StackMark />
          <span className="text-[15px] font-semibold tracking-[0.2em]">EDGE</span>
        </Link>
        {children}
        <div className="ml-auto flex items-center gap-5 text-[13px]">
          {right}
          <ThemeToggle tone="ink" />
        </div>
      </div>
    </header>
  )
}

const ROLE_LABEL: Record<string, string> = { Admin: '관리자', Manager: '매니저', User: '사용자', Viewer: '뷰어' }

export default function PortalLayout({ children }: { children: React.ReactNode }) {
  const { role, scopes, logout, can } = useAuthStore()
  const navigate = useNavigate()

  const handleLogout = () => { logout(); navigate('/login') }

  // App.tsx PortalAdmin 라우트 게이트와 같은 규칙
  const tabs = [
    ...(role === 'Manager' || can(SCOPE_EDGE_USERS) ? [{ to: '/admin/users', label: '사용자' }] : []),
    ...(can(SCOPE_EDGE_COMPANIES) ? [{ to: '/admin/companies', label: '회사' }] : []),
  ]

  return (
    <div className="min-h-screen bg-canvas">
      <EdgeBar right={<>
        <span className="text-white/40 hidden sm:inline">{ROLE_LABEL[role ?? ''] ?? role}{hasEdgeTeamScope(scopes) ? ', Edge팀' : ''}</span>
        <Link to="/apps" className="text-white/65 hover:text-white transition-colors">서비스 선택</Link>
        <button onClick={handleLogout} className="text-white/65 hover:text-white transition-colors">로그아웃</button>
      </>}>
        <nav className="flex self-stretch" aria-label="조직 관리">
          {tabs.map(t => (
            <NavLink key={t.to} to={t.to}
              className={({ isActive }) =>
                `flex items-center px-4 text-sm border-b-2 transition-colors ${
                  isActive
                    ? 'border-lamp-run text-white font-medium'
                    : 'border-transparent text-white/55 hover:text-white/85'
                }`
              }>
              {t.label}
            </NavLink>
          ))}
        </nav>
      </EdgeBar>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {children}
      </main>
    </div>
  )
}
